import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNodeHistory } from '../hooks/useNodeHistory';
import { getPressure } from '../lib/liveSensorMetrics';
import type { IoTNode } from '../lib/iotApi';
import { pressureDuration, pressureFreshnessMs, type PressureDuration } from '../lib/pressureDuration';
import { useIntlFormatting } from '../utils/intl';

export default function CgmPressureStatus({ node, valveState, detailed = false, variant }: {
  node?: IoTNode; valveState?: string; detailed?: boolean; variant?: 'summary' | 'metrics';
}) {
  const { t } = useTranslation(['common', 'agrisafeR1']);
  const { formatNumber, formatDateTime } = useIntlFormatting();
  const { user } = useAuth();
  const authScope = user?.id ? JSON.stringify([user.org_id ?? null, user.id]) : undefined;
  const [now, setNow] = useState(Date.now());
  const serverMode = !!node && Object.prototype.hasOwnProperty.call(node, 'irrigation_summary');
  const summary = user?.id ? node?.irrigation_summary : null;
  const clock = useMemo(() => ({ received: Date.now(), server: summary?.serverTime }), [summary]);
  const displayNow = clock.server === undefined ? now : clock.server + now - clock.received;
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  // Keep the seven-day fallback until server summaries are enabled.
  // Server mode disables this history request entirely.
  const history = useNodeHistory(user?.id && !serverMode ? node?.id ?? null : null, 168, node?.latest?.ts, authScope);
  const historicalObservations = useMemo(() => history.series
    .filter(point => {
      const value = getPressure(point.measures ?? undefined);
      return Number.isFinite(Date.parse(point.ts ?? '')) && value !== undefined && Number.isFinite(value) && value >= 0;
    })
    .map(point => ({ ts: point.ts ?? undefined, measures: point.measures ?? undefined }))
    .sort((a, b) => Date.parse(b.ts!) - Date.parse(a.ts!)), [history.series]);
  // Keep stable observation identities while reconsidering clock-skewed points.
  const latest = node?.latest ?? historicalObservations.find(point => Date.parse(point.ts!) <= now);
  const latestTimestamp = Date.parse(latest?.ts ?? '');
  const freshnessMs = useMemo(() => summary?.freshnessMs ?? pressureFreshnessMs(latest, history.series), [summary, latest, history.series]);
  const latestIsFresh = serverMode
    ? !!summary && displayNow >= summary.observedAt && displayNow - summary.observedAt < freshnessMs
      && (!Number.isFinite(latestTimestamp) || latestTimestamp <= summary.observedAt)
    : Number.isFinite(latestTimestamp) && latestTimestamp <= now && now - latestTimestamp < freshnessMs;
  // Parse/sort history only on new evidence or a freshness transition. The
  // one-second clock below updates elapsed display without redoing that work.
  const result = useMemo<PressureDuration>(() => serverMode
    ? { ...(summary ?? { pressure: null, minimum: true }), state: latestIsFresh ? summary!.state : 'unknown' as const,
        since: latestIsFresh ? summary!.since : null }
    : pressureDuration(latest, history.series, latestIsFresh ? latestTimestamp : Date.now()),
    [serverMode, summary, latest, history.series, latestIsFresh, latestTimestamp]);
  const durationText = (ms: number) => {
    const minutes = Math.max(0, Math.floor(ms / 60000));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor(minutes / 60) % 24;
    return [days ? t('common:cgmDurationDays', { value: days }) : '',
      days || hours ? t('common:cgmDurationHours', { value: hours }) : '',
      t('common:cgmDurationMinutes', { value: minutes % 60 })].filter(Boolean).join(' ');
  };
  // Only the conflict-aware reducer can establish an independent boundary.
  const hasHistory = serverMode ? result.since !== null && !!summary && result.since < summary.observedAt : !history.error && result.since !== null && result.since < latestTimestamp;
  const duration = !hasHistory || result.since === null ? '—' : durationText(displayNow - result.since);
  const noIrrigation24h = !serverMode && !!node?.id && !history.loading && !history.error
    && latestIsFresh && result.state === 'stopped' && !result.estimated
    && result.since !== null && result.since <= now - 24 * 60 * 60 * 1000;
  const since = result.state === 'active' ? t('common:cgmHomeActiveSince', { duration })
    : noIrrigation24h ? t('common:cgmHomeNoIrrigation24h')
      : result.state === 'stopped' ? t('common:cgmHomeStoppedSince', { duration }) : t('agrisafeR1:dashboard.state.unknown');
  const contradiction = result.state === 'active' && valveState === 'closed'
    || result.state === 'stopped' && valveState === 'open';
  const rawPressure = serverMode ? summary?.pressure ?? undefined : getPressure(latest?.measures);
  const pressureConflict = useMemo(() => history.series.some(point => Date.parse(point.ts ?? '') === latestTimestamp
    && getPressure(point.measures ?? undefined) !== rawPressure), [history.series, latestTimestamp, rawPressure]);
  const displayedPressure = (serverMode ? !!summary && summary.observedAt <= displayNow : Number.isFinite(latestTimestamp) && latestTimestamp <= now) && !pressureConflict && rawPressure !== undefined && Number.isFinite(rawPressure) && rawPressure >= 0 ? rawPressure : null;
  const pressureTimestamp = serverMode ? summary?.observedAt : latestTimestamp;
  const pressure = <p>{t('agrisafeR1:dashboard.metrics.pressure')}: {displayedPressure === null ? '—'
    : formatNumber(displayedPressure, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('agrisafeR1:dashboard.units.bar')}{!latestIsFresh && displayedPressure !== null && pressureTimestamp !== undefined && Number.isFinite(pressureTimestamp) && <span className="block text-xs">{formatDateTime(pressureTimestamp)}</span>}</p>;
  const timer = <>
    <p>{since}</p>
    {hasHistory && result.minimum && <p className="text-xs">{t('common:cgmHomeMinimum')}</p>}
    {hasHistory && result.estimated && <p className="text-xs">{t('common:cgmHomeEstimated')}</p>}
    {contradiction && <p className="text-xs">{t('common:cgmHomeValveContradiction')}</p>}
    {result.state === 'active' && valveState !== 'open' && valveState !== 'closed' && <p className="text-xs">{t('common:cgmHomeValveUnavailable')}</p>}
  </>;
  const last = node?.id ? result.lastIrrigation : undefined;
  const historyMessage = !node?.id ? t('common:cgmHistoryNoSensor')
    : serverMode && !summary ? t('common:cgmHistoryRetry')
    : history.error ? t('common:cgmHistoryRetry')
      : history.loading ? t('common:cgmHistoryLoading')
        : noIrrigation24h ? t('common:cgmHomeNoIrrigation24h')
          : result.state === 'active' ? t('common:cgmIrrigationOngoing')
            : t('common:cgmHomeNoCompletedCycle');
  const previous = <div data-testid="last-irrigation" className="space-y-1">
    <p className="text-sm opacity-80">{t('common:cgmHomeLastIrrigation')}</p>
    {last ? <>
      <p className="text-sm">{t('common:cgmLastObservedEnd')}</p>
      <p className="font-semibold" data-testid="last-irrigation-ended-at">{formatDateTime(last.endedAt, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      <p>{t('common:cgmHomeLastDuration', { duration: durationText(last.durationMs) })}</p>
      {last.minimum && <p className="text-xs">{t('common:cgmHomeMinimum')}</p>}
      {last.estimated && <p className="text-xs">{t('common:cgmHomeEstimated')}</p>}
      {(history.loading || history.error) && <p className="text-xs">{t('common:cgmHistoryRetained')}</p>}
    </> : <p>{historyMessage}</p>}
  </div>;
  if (variant === 'summary') return previous;
  if (variant === 'metrics') return <>
    <div className="order-3 rounded-xl bg-white/20 p-3 min-w-0" data-testid="pressure-supervision">{pressure}</div>
    <div className="order-5 rounded-xl bg-white/20 p-3 min-w-0" data-testid="pressure-elapsed">{timer}</div>
  </>;
  return <div className="space-y-1" data-testid="pressure-supervision">
    {pressure}{timer}{detailed && previous}
  </div>;
}
